import React, { lazy as lazyReact, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom';
import { WhatsAppFloat } from './components/WhatsAppFloat';
import { Toaster } from 'sonner';

import CookieConsent from './components/CookieConsent';
import { iniciarPreLeads, registrarPagina } from './lib/preLead';
import { RotaEquipe } from './components/RotaEquipe';

// ── Abrir de primeira: cada pagina tenta baixar ate 3 vezes (internet do celular oscilando);
// se ainda falhar (ex.: o site acabou de ser atualizado), recarrega 1 vez sozinho. Nenhuma tela muda.
function tentarCarregar(carregar, restantes) {
  return carregar().catch((erro) => {
    if (restantes > 1) return new Promise((ok) => { setTimeout(ok, 800); }).then(() => tentarCarregar(carregar, restantes - 1));
    if (window.__icaRecarregarUmaVez && window.__icaRecarregarUmaVez()) return new Promise(() => {});
    throw erro;
  });
}
function lazy(carregar) {
  return lazyReact(() => tentarCarregar(carregar, 3));
}

// No lugar de tela branca: aviso simples com "Tocar para tentar de novo".
class AberturaSegura extends React.Component {
  constructor(props) { super(props); this.state = { erro: false }; }
  static getDerivedStateFromError() { return { erro: true }; }
  componentDidCatch(erro) {
    try { console.error('Falha ao abrir a pagina:', erro); } catch (e) { void e; }
    const nome = String((erro && (erro.name || erro.message)) || '');
    if (/ChunkLoadError|Loading chunk|Loading CSS chunk/i.test(nome) && window.__icaRecarregarUmaVez) window.__icaRecarregarUmaVez();
  }
  render() {
    if (!this.state.erro) return this.props.children;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-white px-6 text-center">
        <p className="text-slate-700">A página não abriu por completo. Verifique sua internet.</p>
        <button type="button" onClick={() => window.location.reload()} data-testid="abrir-de-novo"
          className="bg-primary text-white px-5 py-2.5 rounded-[10px] font-medium hover:opacity-90">Tocar para tentar de novo</button>
      </div>
    );
  }
}

// ── Scroll para o topo a cada mudança de rota ─────────────────────────────
function ScrollToTop() {
  const location = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    const chave = 'scroll:' + (location.key || location.pathname);
    if (navType !== 'POP') {
      window.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }
    const salvo = sessionStorage.getItem(chave);
    if (salvo === null) return;
    const y = parseInt(salvo, 10) || 0;
    if (y <= 0) return;
    let tempo = 0;
    const intervalo = setInterval(() => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      tempo += 50;
      if (max >= y - 2 || tempo >= 3000) {
        window.scrollTo({ top: y, behavior: 'instant' });
        clearInterval(intervalo);
      }
    }, 50);
    return () => clearInterval(intervalo);
  }, [location, navType]);

  useEffect(() => {
    const chave = 'scroll:' + (location.key || location.pathname);
    // So grava se o historico ainda esta nesta entrada (evita gravar a rolagem "encolhida" da troca de pagina).
    const aoRolar = () => {
      const atual = (window.history.state && window.history.state.key) || 'default';
      if (atual !== (location.key || 'default')) return;
      sessionStorage.setItem(chave, String(window.scrollY));
    };
    window.addEventListener('scroll', aoRolar, { passive: true });
    return () => window.removeEventListener('scroll', aoRolar);
  }, [location]);

  return null;
}

// ── Pre-Leads: paginas visitadas (so informa o caminho de cada pagina aberta; nao muda nada na tela) ──
function PaginasVisitadas() {
  const location = useLocation();
  useEffect(() => {
    const t = setTimeout(() => registrarPagina(location), 400);
    return () => clearTimeout(t);
  }, [location]);
  return null;
}

// ── Spinner de loading ────────────────────────────────────────────────────
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full" />
  </div>
);

// ── Páginas públicas — lazy loading (cada página carrega só quando acessada)
const Home               = lazy(() => import('./pages/Home').then(m => ({ default: m.Home })));
const ImoveisAvulsos     = lazy(() => import('./pages/ImoveisAvulsos').then(m => ({ default: m.ImoveisAvulsos })));
// Endereco antigo da lista de imoveis prontos (/imoveis-avulsos): leva para /imoveis mantendo os parametros do link (utm, pagina, filtros)
function IrParaImoveis() {
  const l = useLocation();
  return <Navigate to={'/imoveis' + (l.search || '') + (l.hash || '')} replace />;
}
const Lancamentos        = lazy(() => import('./pages/Lancamentos').then(m => ({ default: m.Lancamentos })));
const CadastroImovel     = lazy(() => import('./pages/CadastroImovel').then(m => ({ default: m.CadastroImovel })));
const ListaTransmissao   = lazy(() => import('./pages/ListaTransmissao').then(m => ({ default: m.ListaTransmissao })));
const PoliticasPrivacidade = lazy(() => import('./pages/PoliticasPrivacidade').then(m => ({ default: m.PoliticasPrivacidade })));
const PropertyDetails    = lazy(() => import('./pages/PropertyDetails').then(m => ({ default: m.PropertyDetails })));
const VitrinePublica     = lazy(() => import('./pages/VitrinePublica').then(m => ({ default: m.VitrinePublica })));
const AnonymousProperty  = lazy(() => import('./pages/AnonymousProperty').then(m => ({ default: m.AnonymousProperty })));
const NaoEncontrada     = lazy(() => import('./pages/NaoEncontrada').then(m => ({ default: m.NaoEncontrada })));

// ── Páginas admin — lazy loading ──────────────────────────────────────────
const AdminLogin         = lazy(() => import('./pages/admin/AdminLogin').then(m => ({ default: m.AdminLogin })));
const AdminDashboard     = lazy(() => import('./pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const AdminPedidos       = lazy(() => import('./pages/admin/AdminPedidos').then(m => ({ default: m.PedidosComAbas })));
const AdminImoveis       = lazy(() => import('./pages/admin/AdminImoveis').then(m => ({ default: m.AdminImoveis })));
const AdminLancamentos   = lazy(() => import('./pages/admin/AdminLancamentos').then(m => ({ default: m.AdminLancamentos })));
const AdminImovelForm    = lazy(() => import('./pages/admin/AdminImovelForm').then(m => ({ default: m.AdminImovelForm })));
const AdminNewsletter    = lazy(() => import('./pages/admin/AdminNewsletter').then(m => ({ default: m.AdminNewsletter })));
const AdminConfiguracoes = lazy(() => import('./pages/admin/AdminConfiguracoes').then(m => ({ default: m.AdminConfiguracoes })));
const AdminAgenda        = lazy(() => import('./pages/admin/AdminAgenda').then(m => ({ default: m.AdminAgenda })));
const AdminFunil         = lazy(() => import('./pages/admin/AdminFunil').then(m => ({ default: m.AdminFunil })));
const AdminRecursos      = lazy(() => import('./pages/admin/AdminRecursos').then(m => ({ default: m.AdminRecursos })));

// ── Proteção de rotas admin ───────────────────────────────────────────────
function ProtectedRoute({ children }) {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/admin/login" replace />;
  return <RotaEquipe>{children}</RotaEquipe>;
}

function App() {
  // Pre-Leads: guarda de onde o visitante veio e poe o codigo (Ref.) na mensagem do WhatsApp (nao age no admin)
  useEffect(() => { iniciarPreLeads(); }, []);
  return (
    <Router>
      <ScrollToTop />
      <PaginasVisitadas />
      <Toaster position="top-right" richColors />
      <AberturaSegura>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* ── Públicas ── */}
          <Route path="/"                                   element={<Home />} />
          <Route path="/imoveis"                            element={<ImoveisAvulsos />} />
          <Route path="/imoveis-avulsos"                    element={<IrParaImoveis />} />
          <Route path="/lancamentos"                        element={<Lancamentos />} />
          <Route path="/imovel/:id"                         element={<PropertyDetails />} />
          <Route path="/imoveis/:transacao"                             element={<ImoveisAvulsos />} />
          <Route path="/imoveis/:transacao/:bairro"                     element={<ImoveisAvulsos />} />
          <Route path="/imoveis/:transacao/:bairro/:tipo"               element={<ImoveisAvulsos />} />
          <Route path="/imoveis/:transacao/:bairro/:tipo/:quartos"      element={<ImoveisAvulsos />} />
          <Route path="/vitrine/:slug"                      element={<VitrinePublica />} />
          <Route path="/cadastro-imovel"                    element={<CadastroImovel />} />
          <Route path="/grupo-corretores"                   element={<ListaTransmissao />} />
          <Route path="/lista-transmissao"                  element={<Navigate to="/grupo-corretores" replace />} />
          <Route path="/p/:token"                           element={<AnonymousProperty />} />
          <Route path="/politicas-de-privacidade-e-cookies" element={<PoliticasPrivacidade />} />

          {/* ── Admin ── */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/dashboard"
            element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/pedidos"
            element={<ProtectedRoute><AdminPedidos /></ProtectedRoute>} />
          <Route path="/admin/imoveis"
            element={<ProtectedRoute><AdminImoveis /></ProtectedRoute>} />
          <Route path="/admin/imoveis/novo"
            element={<ProtectedRoute><AdminImovelForm /></ProtectedRoute>} />
          <Route path="/admin/imoveis/editar/:id"
            element={<ProtectedRoute><AdminImovelForm /></ProtectedRoute>} />
          <Route path="/admin/lancamentos"
            element={<ProtectedRoute><AdminLancamentos /></ProtectedRoute>} />
          <Route path="/admin/lancamentos/novo"
            element={<ProtectedRoute><AdminImovelForm /></ProtectedRoute>} />
          <Route path="/admin/lancamentos/editar/:id"
            element={<ProtectedRoute><AdminImovelForm /></ProtectedRoute>} />
          <Route path="/admin/newsletter"
            element={<ProtectedRoute><AdminNewsletter /></ProtectedRoute>} />
          <Route path="/admin/configuracoes"
            element={<ProtectedRoute><AdminConfiguracoes /></ProtectedRoute>} />
          <Route path="/admin/agenda"
            element={<ProtectedRoute><AdminAgenda /></ProtectedRoute>} />
          <Route path="/admin/funil"
            element={<ProtectedRoute><AdminFunil /></ProtectedRoute>} />
          <Route path="/admin/recursos"
            element={<ProtectedRoute><AdminRecursos /></ProtectedRoute>} />

          {/* ── 404 ── */}
          <Route path="*" element={<NaoEncontrada />} />
        </Routes>
        <WhatsAppFloat />
      </Suspense>
      </AberturaSegura>
      <CookieConsent />
    </Router>
  );
}

export default App;